"use client";

import type { ReactNode } from "react";
import { cn } from "../../lib";
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from "../input-otp";
import {
	ariaInvalidFromError,
	buildControlDescribedBy,
} from "./field-a11y";
import { FieldShell, type FieldLayoutHint } from "./field-shell";

export interface OtpFieldProps {
	id: string;
	label: string;
	value: string;
	onChange: (value: string) => void;
	description?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	layoutHint?: FieldLayoutHint;
	className?: string;
	name?: string;
	onBlur?: () => void;
	disabled?: boolean;
	/** Digit count (default 6). */
	length?: number;
	autoComplete?: string;
	hideLabel?: boolean;
}

function buildSlotIndexes(length: number): number[] {
	return Array.from({ length }, (_, index) => index);
}

/**
 * OTP digit FieldType. Uses InputOTP primitives as implementation detail.
 */
export function OtpField({
	id,
	label,
	value,
	onChange,
	description,
	error,
	reserveErrorSpace = false,
	layoutHint,
	className,
	name,
	onBlur,
	disabled = false,
	length = 6,
	autoComplete = "one-time-code",
	hideLabel = false,
}: OtpFieldProps) {
	const safeLength = Math.min(12, Math.max(4, length));
	const indexes = buildSlotIndexes(safeLength);
	const mid = Math.ceil(safeLength / 2);
	const left = indexes.slice(0, mid);
	const right = indexes.slice(mid);
	const controlDescribedBy = buildControlDescribedBy({
		id,
		error,
		hasDescription: Boolean(description),
	});

	return (
		<FieldShell
			id={id}
			label={hideLabel ? undefined : label}
			description={description}
			error={error}
			reserveErrorSpace={reserveErrorSpace}
			layoutHint={layoutHint}
			className={className}
		>
			<InputOTP
				id={id}
				name={name}
				maxLength={safeLength}
				value={value}
				disabled={disabled}
				autoComplete={autoComplete}
				onBlur={onBlur}
				onChange={onChange}
				aria-label={hideLabel ? label : undefined}
				aria-invalid={ariaInvalidFromError(error)}
				aria-describedby={controlDescribedBy}
				containerClassName={cn("justify-center")}
			>
				<InputOTPGroup>
					{left.map((index) => (
						<InputOTPSlot
							key={index}
							index={index}
							className="size-10 text-lg"
						/>
					))}
				</InputOTPGroup>
				{right.length > 0 ? (
					<>
						<InputOTPSeparator className="opacity-40" />
						<InputOTPGroup>
							{right.map((index) => (
								<InputOTPSlot
									key={index}
									index={index}
									className="size-10 text-lg"
								/>
							))}
						</InputOTPGroup>
					</>
				) : null}
			</InputOTP>
		</FieldShell>
	);
}
